/* eslint-disable max-lines */
import styled, { css } from "styled-components";
import {
  ActionsGroup,
  Description,
  FlatButton,
  H1,
  H2,
  IconButton,
  IconButtonButton,
  IconButtonInnerChild,
  IThemeConstants,
  transition,
} from "@evergis/ui";

import { Flex } from "../../components/styled/layout/Flex";
import { FormContainer } from "../../components/styled/forms";
import { CenteredContentPage } from "../../components/styled/layout/CenteredContentPage";
import { CheckboxesContainer } from "../sign-up/styled";
import { stylesFor } from "../../styles/queries";

const BgImgMixin = css<{ bgImg?: string }>`
  background: url(${({ bgImg }) => bgImg}) scroll 0 0 no-repeat;
  background-size: cover;
`;

const OverlayMixin = css<{ overlay?: string }>`
  :after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ overlay }) => overlay};
  }
`;

export const FooterLogoContainer = styled(Flex)<{ hasLogoLink?: boolean }>`
  line-height: 0.75rem;
  align-items: center;

  a {
    margin-left: 1.5rem;
    color: ${({ theme: { palette } }) => palette.textSecondary};
    transition: color ${transition.hover};

    :hover {
      color: ${({ theme: { palette } }) => palette.textPrimary};
    }
  }

  ${({ hasLogoLink }) =>
    hasLogoLink &&
    css`
      img {
        padding-right: 1.5rem;
        border-right: 1px solid ${({ theme: { palette } }) => palette.textPrimary};
      }
    `}
`;

const SignInPageLaptopMixin = css`
  align-items: flex-start;

  && {
    flex-direction: column;
  }

  > ${FooterLogoContainer} .footer-image {
    display: block;
    visibility: visible;
  }
`;

export const SignInFormContainer = styled(FormContainer)`
  justify-content: center;
  width: 22.5rem;
  padding: 2.5rem;
  background-color: rgba(255, 253, 246, 0.9);
  backdrop-filter: blur(20px);
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.large};

  ${stylesFor.mobile} {
    min-width: 19rem;
  }

  input[type="text"],
  input[type="password"] {
    background: white;
  }
`;

export const SignInPageContainer = styled(CenteredContentPage)<{
  bgImg?: string;
  overlay?: string;
  palette?: IThemeConstants["palette"];
}>`
  position: relative;
  justify-content: space-between;
  width: auto;
  padding: 0 5rem;
  overflow: hidden;

  ${({ bgImg }) => !!bgImg && BgImgMixin};
  ${({ overlay }) => !!overlay && OverlayMixin};

  .background-video {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    object-fit: cover;
  }

  > ${FooterLogoContainer} {
    margin-top: 2rem;

    .footer-image {
      z-index: 1;
      position: relative;
      display: none;
      visibility: hidden;
    }
  }

  ${stylesFor.laptop} {
    ${SignInPageLaptopMixin};
  }

  ${stylesFor.mobile} {
    ${SignInPageLaptopMixin};
  }

  @media (max-height: 800px) {
    align-items: flex-start;
    padding-top: 4rem;
  }

  ${({ palette }) =>
    !!palette?.primary &&
    css`
      ${SignInFormContainer} {
        a,
        a:hover,
        a > button {
          color: ${palette.primary};
        }

        .primary-button:not(:disabled) {
          background-color: ${palette.primary};
        }
      }

      ${CheckboxesContainer} {
        svg rect {
          fill: ${palette.primary} !important;
        }
      }
    `}
`;

export const SocialNetworks = styled(Flex)`
  flex-direction: column;
  align-items: center;

  &&& {
    margin-bottom: 0;
  }

  a > button > span[kind]:after {
    font-size: 1.25rem;
  }
`;

export const LoginWithContainer = styled(Description)`
  margin-top: 1.5rem;
  margin-bottom: 0;
`;

export const SignInTitle = styled(H1)`
  margin: 0;
  font-size: 2.25rem;
`;

export const SignInFormHeader = styled(Flex)`
  justify-content: space-between;
  width: 100%;
  margin-bottom: 1.5rem;
`;

const SignInSideContainer = styled(Flex)`
  z-index: 1;
  position: relative;
  flex-direction: column;
  justify-content: center;
  flex: 1;
`;

const SignInLeftLaptopMixin = css`
  padding-right: 0;
  padding-bottom: 3rem;
  align-items: center;

  .footer-image {
    display: none;
    visibility: hidden;
  }
`;

export const SignInLeftContainer = styled(SignInSideContainer)`
  flex-wrap: nowrap;
  justify-content: space-between;
  height: calc(100% - 10rem);
  padding: 5rem 2.5rem 5rem 0;

  ${FooterLogoContainer} {
    margin-top: 3.5rem;
  }

  ${stylesFor.desktop} {
    height: auto;
  }

  ${stylesFor.laptop} {
    ${SignInLeftLaptopMixin};
  }

  ${stylesFor.mobile} {
    ${SignInLeftLaptopMixin};
  }
`;

export const SignInRightContainer = styled(SignInSideContainer)`
  align-items: center;
  margin: 0 auto;
`;

export const SignInLeftContent = styled.div`
  max-width: 42.5rem;
  margin-top: auto;
`;

type TextProps = {
  fontSize?: string | number;
  fontColor?: string;
  fontFamily?: string;
};

const DEFAULT_FONT_FAMILIES = "'Roboto', Tahoma";

export const SignInLeftTitle = styled(H1)<TextProps>`
  margin: 3rem 0 0;
  font-size: ${({ fontSize }) => (fontSize ? `${fontSize}px` : "4.5rem")};
  color: ${({ theme: { palette }, fontColor }) => fontColor || palette.textContrast};

  ${({ fontFamily }) => !!fontFamily && `font-family: ${fontFamily}, ${DEFAULT_FONT_FAMILIES};`}
  ${stylesFor.desktop} {
    font-size: 3.5rem;
  }

  ${stylesFor.laptop} {
    text-align: center;
    font-size: 3.5rem;
  }

  ${stylesFor.mobile} {
    text-align: center;
    font-size: 3.5rem;
  }
`;

export const SignInLeftDescription = styled(H2)<TextProps>`
  margin: 2rem 0 0;
  color: ${({ theme: { palette }, fontColor }) => fontColor || palette.textContrast};
  ${({ fontFamily }) => !!fontFamily && `font-family: ${fontFamily}, ${DEFAULT_FONT_FAMILIES};`}
  line-height: 2rem;
`;

export const SendConfirmEmailButton = styled(FlatButton)`
  margin-top: 0.5rem;
  text-transform: none;
  font-size: 0.75rem;
`;
SendConfirmEmailButton.defaultProps = {
  primary: true,
};

export const FooterButtons = styled(Flex)`
  align-items: center;
  height: 2rem;
  margin-top: 2rem;

  ${IconButtonButton} {
    opacity: 0.8;
    transition: ${transition.hover} opacity;

    :hover {
      opacity: 1;

      ${IconButtonInnerChild} {
        color: ${({ theme: { palette } }) => palette.textContrast};
      }
    }
  }

  ${IconButtonInnerChild} {
    color: ${({ theme: { palette } }) => palette.textContrast};
  }

  span[kind]:after {
    font-size: 1.25rem;
    color: ${({ theme: { palette } }) => palette.textContrast};
  }
`;

export const SocialButtons = styled(Flex)`
  align-items: center;
  height: inherit;

  a {
    width: 1.25rem;
    margin-right: 1.25rem;

    ${IconButtonButton} {
      span[kind] {
        width: 1.25rem;
      }
    }
  }
`;

export const LinkButton = styled(IconButton)`
  margin-top: 0.25rem;

  span[kind]:after {
    font-size: 1rem;
  }
`;

export const SocialDivider = styled.div`
  width: 0;
  height: 1.25rem;
  margin: 0.25rem 1.5rem 0 0.5rem;
  border-right: 1px ${({ theme: { palette } }) => palette.textContrast} solid;
  opacity: 0.28;
`;

export const LogoContainer = styled(Flex)`
  align-items: center;
`;

export const LogoLink = styled.a`
  display: flex;
  align-items: center;
  color: inherit;
  text-decoration: none;
`;

export const LogoTextContainer = styled.div<TextProps>`
  max-width: 17.5rem;
  margin-left: 1rem;
  padding-left: 1rem;
  border-left: 1px rgba(17, 37, 47, 0.12) solid;
  color: ${({ theme: { palette }, fontColor }) => fontColor || palette.textContrast};
  ${({ fontFamily }) => !!fontFamily && `font-family: ${fontFamily}, ${DEFAULT_FONT_FAMILIES};`}
`;

export const DialogActionsGroup = styled(ActionsGroup)`
  && {
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    width: 100%;
  }
`;

export const AboutDialogText = styled.div<{ linkColor?: string }>`
  font-size: 1rem;
  font-family: "Nunito Sans", ${DEFAULT_FONT_FAMILIES};
  line-height: 1.125rem;

  a {
    color: ${({ theme: { palette }, linkColor }) => linkColor ?? palette.primary};
  }
`;
